import { type ReactNode, type SubmitEventHandler } from "react";
import { Link } from "react-router-dom";
import { FileText, Loader2, SlidersHorizontal, User as UserIcon, Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage } from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupLabel, SidebarInset, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";

const SECTIONS = [
    { id: "campaign-basics", label: "Campaign Basics", icon: UserIcon },
    { id: "brief", label: "Brief", icon: FileText },
    { id: "eligibility", label: "Creator Eligibility", icon: Users },
    { id: "extras", label: "Extras", icon: SlidersHorizontal },
] as const;

type FormSectionId = (typeof SECTIONS)[number]["id"];

type CampaignFormShellProps = {
    breadcrumbName: string;
    campaignHref?: string;
    isSubmitting: boolean;
    disabled: boolean;
    submitLabel: string;
    onSubmit: SubmitEventHandler<HTMLFormElement>;
    children: ReactNode;
};

/** Sidebar, breadcrumb, and Publish/Save bar. The section list sticks, and each link jumps to its section. */
export function CampaignFormShell({
    breadcrumbName,
    campaignHref,
    isSubmitting,
    disabled,
    submitLabel,
    onSubmit,
    children,
}: CampaignFormShellProps) {
    function scrollToSection(id: FormSectionId) {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    return (
        <SidebarProvider className="relative min-h-[calc(100vh-10rem)] min-w-0 overflow-x-clip">
            <Sidebar className="absolute inset-y-0 h-full">
                <SidebarContent className="sticky top-0 z-10 h-auto flex-none self-start overflow-visible bg-sidebar">
                    <SidebarGroup>
                        <SidebarGroupLabel>Settings</SidebarGroupLabel>
                        <SidebarMenu>
                            {SECTIONS.map((section) => {
                                const Icon = section.icon;
                                return (
                                    <SidebarMenuItem key={section.id}>
                                        <SidebarMenuButton onClick={() => scrollToSection(section.id)}>
                                            <Icon />
                                            <span>{section.label}</span>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                );
                            })}
                        </SidebarMenu>
                    </SidebarGroup>
                </SidebarContent>
            </Sidebar>
            <SidebarInset className="min-w-0 bg-gray-100">
                <form id="campaign-form" onSubmit={onSubmit}>
                    <header className="sticky top-0 z-10 flex h-16 shrink-0 items-center border-b bg-background px-4">
                        {/* Trigger, rule, and crumbs share one row opposite Publish/Save. */}
                        <div className="flex items-center gap-2">
                            <SidebarTrigger className="size-7" />
                            <Separator
                                orientation="vertical"
                                className="mr-2 data-vertical:h-4 data-vertical:self-center"
                            />
                            <Breadcrumb>
                                <BreadcrumbList className="gap-1.5">
                                    <BreadcrumbItem>
                                        {campaignHref ? (
                                            <BreadcrumbLink render={<Link to={campaignHref} />}>
                                                {breadcrumbName}
                                            </BreadcrumbLink>
                                        ) : (
                                            <BreadcrumbPage>{breadcrumbName}</BreadcrumbPage>
                                        )}
                                    </BreadcrumbItem>
                                </BreadcrumbList>
                            </Breadcrumb>
                        </div>
                        <Button
                            type="submit"
                            className="ml-auto"
                            disabled={disabled}
                        >
                            {isSubmitting ? (
                                <Loader2 className="size-4 animate-spin" aria-label="Saving" />
                            ) : null}
                            {submitLabel}
                        </Button>
                    </header>

                    <div className="p-4">
                        <div className="mx-auto max-w-xl space-y-12">
                            {children}
                        </div>
                    </div>
                </form>
            </SidebarInset>
        </SidebarProvider>
    );
}
